import styled, { keyframes, css } from 'styled-components';
import backgroundImage from '../../assets/images/background.webp';

// ========================================
// Animations
// ========================================

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.5);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.8;
  }
`;

// ========================================
// Main Container
// ========================================

export const PageContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  background: #f5f5f5;
`;

// ========================================
// Hero Section
// ========================================

export const HeroSection = styled.section`
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  width: 100%;
  padding: 2rem;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(30, 76, 92, 0.85) 0%,
      rgba(217, 125, 43, 0.75) 100%
    );
    z-index: 1;
  }
`;

export const HeroBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: url(${backgroundImage});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  
  @media (max-width: 768px) {
    background-attachment: scroll;
  }
`;

export const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  text-align: center;
  max-width: 700px;
  width: 100%;
  margin: 0 auto;
  animation: ${fadeInUp} 0.8s ease-out;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.95);
  padding: 2.5rem 2rem;
  border-radius: 24px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
    max-width: 90%;
  }
  
  @media (max-width: 480px) {
    padding: 1.5rem 1.25rem;
  }
`;

// ========================================
// Status Icon
// ========================================

export const StatusIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1rem;
  animation: ${scaleIn} 0.5s ease-out;
  
  ${props => {
    switch (props.$status) {
      case 'success':
        return css`
          color: #28a745;
          filter: drop-shadow(0 4px 12px rgba(40, 167, 69, 0.3));
        `;
      case 'pending':
        return css`
          color: #ffc107;
          filter: drop-shadow(0 4px 12px rgba(255, 193, 7, 0.3));
          animation: ${scaleIn} 0.5s ease-out, ${pulse} 2s ease-in-out infinite;
        `;
      case 'failure':
        return css`
          color: #dc3545;
          filter: drop-shadow(0 4px 12px rgba(220, 53, 69, 0.3));
        `;
      case 'waiting':
        return css`
          color: #6c757d;
          filter: drop-shadow(0 4px 12px rgba(108, 117, 125, 0.3));
          animation: ${scaleIn} 0.5s ease-out, ${pulse} 2s ease-in-out infinite;
        `;
      default:
        return '';
    }
  }}
  
  @media (max-width: 768px) {
    font-size: 4rem;
  }
  
  @media (max-width: 480px) {
    font-size: 3.5rem;
  }
`;

// ========================================
// Text Elements
// ========================================

export const Title = styled.h1`
  color: #1E4C5C;
  font-size: clamp(1.6rem, 5vw, 2.2rem);
  font-weight: 800;
  margin-bottom: 0.75rem;
  letter-spacing: -0.5px;
  line-height: 1.2;
  animation: ${fadeIn} 0.8s ease-out 0.2s both;
`;

export const Message = styled.p`
  color: #333;
  font-size: clamp(0.95rem, 2.5vw, 1.1rem);
  margin-bottom: 1rem;
  line-height: 1.5;
  font-weight: 500;
  animation: ${fadeIn} 0.8s ease-out 0.3s both;
`;

export const SubMessage = styled.p`
  color: #666;
  font-size: clamp(0.85rem, 2vw, 0.95rem);
  margin-bottom: 1rem;
  line-height: 1.4;
  animation: ${fadeIn} 0.8s ease-out 0.4s both;
`;

export const InfoList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 1rem 0;
  text-align: left;
  width: 100%;
  animation: ${fadeIn} 0.8s ease-out 0.5s both;
`;

export const InfoItem = styled.li`
  color: #333;
  font-size: 0.95rem;
  line-height: 1.6;
  padding: 0.35rem 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  gap: 0.75rem;
  
  svg {
    color: #1E4C5C;
    font-size: 1.1rem;
    flex-shrink: 0;
  }
  
  &:last-child {
    border-bottom: none;
  }
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
    padding: 0.3rem 0;
    gap: 0.6rem;
    
    svg {
      font-size: 1rem;
    }
  }
`;

export const PaymentCode = styled.div`
  background: rgba(30, 76, 92, 0.05);
  border: 2px solid rgba(30, 76, 92, 0.2);
  border-radius: 12px;
  padding: 0.875rem 1.25rem;
  margin: 1rem 0 0.75rem 0;
  font-size: 0.95rem;
  color: #1E4C5C;
  font-family: 'Courier New', monospace;
  word-break: break-all;
  animation: ${fadeIn} 0.8s ease-out 0.6s both;
  
  strong {
    font-weight: 700;
    display: inline;
    font-family: inherit;
    margin-right: 0.5rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.85rem;
    padding: 0.75rem 1rem;
  }
`;

// ========================================
// Loading Spinner
// ========================================

export const LoadingSpinner = styled.div`
  width: 50px;
  height: 50px;
  margin: 2rem auto;
  border: 4px solid rgba(108, 117, 125, 0.2);
  border-top-color: #6c757d;
  border-radius: 50%;
  animation: ${rotate} 1s linear infinite;
`;

// ========================================
// Buttons
// ========================================

export const ButtonGroup = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  gap: 1.5rem;
  width: 100%;
  margin-top: 1.5rem;
  animation: ${fadeIn} 0.8s ease-out 0.7s both;
  
  @media (max-width: 480px) {
    gap: 1rem;
  }
`;

export const WhatsAppButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: rgba(37, 211, 102, 0.1);
  border-radius: 50px;
  color: #25D366;
  text-decoration: none;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  border: 2px solid rgba(37, 211, 102, 0.2);
  cursor: pointer;
  
  svg {
    font-size: 1.5rem;
    transition: transform 0.3s ease;
  }
  
  span {
    font-size: 0.95rem;
    font-weight: 600;
  }
  
  &:hover {
    transform: translateY(-3px) scale(1.05);
    background: #25D366;
    color: white;
    border-color: #25D366;
    box-shadow: 0 8px 20px rgba(37, 211, 102, 0.5);
    
    svg {
      transform: scale(1.1);
    }
  }
  
  &:active {
    transform: translateY(-1px) scale(1.03);
  }
  
  @media (max-width: 768px) {
    padding: 0.65rem 1.25rem;
    gap: 0.45rem;
    
    svg {
      font-size: 1.3rem;
    }
    
    span {
      font-size: 0.85rem;
    }
  }
  
  @media (max-width: 480px) {
    padding: 0.6rem 1rem;
    gap: 0.4rem;
    
    svg {
      font-size: 1.2rem;
    }
    
    span {
      font-size: 0.8rem;
    }
  }
`;

export const RetryButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 1rem 2.5rem;
  background: linear-gradient(135deg, #D97D2B 0%, #F5A64A 100%);
  color: white;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 50px;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 8px 20px rgba(217, 125, 43, 0.4);
  border: 2px solid rgba(255, 255, 255, 0.3);
  cursor: pointer;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 30px rgba(217, 125, 43, 0.6);
    background: linear-gradient(135deg, #F5A64A 0%, #D97D2B 100%);
  }
  
  &:active {
    transform: translateY(-1px);
  }
  
  @media (max-width: 768px) {
    padding: 0.9rem 2rem;
    font-size: 0.95rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.85rem 1.5rem;
    font-size: 0.9rem;
  }
`;

